import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { getSubdomainSlug } from './lib/subdomain';
// Customer Pages
import { HomePage } from './pages/HomePage';
import { RestaurantListingPage } from './pages/RestaurantListingPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { MenuPage } from './pages/MenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { TrackingPage } from './pages/TrackingPage';
import { TablePage } from './pages/TablePage';
// Admin Pages
import { LoginPage } from './pages/admin/LoginPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { KDSPage } from './pages/admin/KDSPage';
import { InventoryPage } from './pages/admin/InventoryPage';
import { MetricsPage } from './pages/admin/MetricsPage';
import { MenuManagementPage } from './pages/admin/MenuManagementPage';
import { ImportPage } from './pages/admin/ImportPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
/** Redirects /admin/:slug → /admin/:slug/dashboard */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/dashboard`} replace data-id="element-0" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/dashboard" replace data-id="element-1" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-2">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <Routes data-id="element-3">
        {/* Public restaurant page at root */}
        <Route path="/" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-5" />} data-id="element-4" />

        {/* Cart & checkout still work */}
        <Route path="/cart" element={<CartPage data-id="element-7" />} data-id="element-6" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-9" />} data-id="element-8" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-11" />} data-id="element-10" />
        <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-13" />} data-id="element-12" />

        {/* Table ordering */}
        <Route path="/table/:tableNumber" element={<TablePage data-id="element-15" />} data-id="element-14" />

        {/* Admin routes — no :slug needed in path, resolved from subdomain */}
        <Route path="/admin/login" element={<LoginPage subdomainSlug={subdomainSlug} data-id="element-17" />} data-id="element-16" />
        <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-19" />} data-id="element-18" />
        <Route path="/admin/dashboard" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-21">
              <DashboardPage data-id="element-22" />
            </SubdomainTenantProvider>} data-id="element-20" />
        <Route path="/admin/kds" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-24">
              <KDSPage data-id="element-25" />
            </SubdomainTenantProvider>} data-id="element-23" />
        <Route path="/admin/menu" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-27">
              <MenuManagementPage data-id="element-28" />
            </SubdomainTenantProvider>} data-id="element-26" />
        <Route path="/admin/inventory" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-30">
              <InventoryPage data-id="element-31" />
            </SubdomainTenantProvider>} data-id="element-29" />
        <Route path="/admin/metrics" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-33">
              <MetricsPage data-id="element-34" />
            </SubdomainTenantProvider>} data-id="element-32" />

        {/* Add import route for subdomain mode */}
        <Route path="/admin/import" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-36">
              <ImportPage data-id="element-37" />
            </SubdomainTenantProvider>} data-id="element-35" />

        {/* Fallback: also support legacy path-based admin on subdomain */}
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-39" />} data-id="element-38" />
        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-41" />} data-id="element-40" />
        <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-43">
              <DashboardPage data-id="element-44" />
            </TenantProvider>} data-id="element-42" />
        <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-46">
              <KDSPage data-id="element-47" />
            </TenantProvider>} data-id="element-45" />
        <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-49">
              <MenuManagementPage data-id="element-50" />
            </TenantProvider>} data-id="element-48" />
        <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-52">
              <InventoryPage data-id="element-53" />
            </TenantProvider>} data-id="element-51" />
        <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-55">
              <MetricsPage data-id="element-56" />
            </TenantProvider>} data-id="element-54" />

        {/* Add legacy import route on subdomain */}
        <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-58">
              <ImportPage data-id="element-59" />
            </TenantProvider>} data-id="element-57" />

        {/* Catch-all: any other path on subdomain → public menu */}
        <Route path="*" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-61" />} data-id="element-60" />
      </Routes>;
  }
  // ─── STANDARD PATH-BASED MODE ───
  // When accessed via noiza.estrategiasdigitalesaz.com/:slug
  return <Routes data-id="element-62">
      {/* Customer Facing */}
      <Route path="/" element={<HomePage data-id="element-64" />} data-id="element-63" />
      <Route path="/restaurants" element={<RestaurantListingPage data-id="element-66" />} data-id="element-65" />
      <Route path="/r/:slug" element={<RestaurantPage data-id="element-68" />} data-id="element-67" />
      <Route path="/r/:slug/menu" element={<MenuPage data-id="element-70" />} data-id="element-69" />
      <Route path="/r/:slug/table/:tableNumber" element={<TablePage data-id="element-72" />} data-id="element-71" />
      <Route path="/cart" element={<CartPage data-id="element-74" />} data-id="element-73" />
      <Route path="/checkout" element={<CheckoutPage data-id="element-76" />} data-id="element-75" />
      <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-78" />} data-id="element-77" />
      <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-80" />} data-id="element-79" />

      {/* Admin - Login */}
      <Route path="/admin/:slug/login" element={<LoginPage data-id="element-82" />} data-id="element-81" />

      {/* Admin - Protected by TenantProvider */}
      <Route path="/admin/:slug" element={<AdminRedirect data-id="element-84" />} data-id="element-83" />
      <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-86">
            <DashboardPage data-id="element-87" />
          </TenantProvider>} data-id="element-85" />
      <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-89">
            <KDSPage data-id="element-90" />
          </TenantProvider>} data-id="element-88" />
      <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-92">
            <MenuManagementPage data-id="element-93" />
          </TenantProvider>} data-id="element-91" />
      <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-95">
            <InventoryPage data-id="element-96" />
          </TenantProvider>} data-id="element-94" />
      <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-98">
            <MetricsPage data-id="element-99" />
          </TenantProvider>} data-id="element-97" />

      {/* Add import route for path-based mode */}
      <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-101">
            <ImportPage data-id="element-102" />
          </TenantProvider>} data-id="element-100" />

      {/* Public Restaurant Ordering Page (Must be last) */}
      <Route path="/:slug" element={<PublicMenuPage data-id="element-104" />} data-id="element-103" />
    </Routes>;
}
export function App() {
  return <CartProvider data-id="element-105">
      <BrowserRouter data-id="element-106">
        <div className="min-h-screen flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-107">
          <AppRoutes data-id="element-108" />
        </div>
      </BrowserRouter>
    </CartProvider>;
}